/* ==========================================================================
   Desktop Sticky Cart — pharmacy / prescription (Rx) module.

   Rx items are products tagged "Veterinary Diet" (detected server-side). This
   module fills the vet-call copy from the SAME pharmacy appointment-status API
   the existing cart uses, and drives the upload modal: validate (JPG/JPEG/PDF
   ≤8MB) → POST to the SAME upload endpoint → attach the returned URL to the Rx
   line items via /cart/change.js (properties.prescription_file) → refresh.

   Parallel build, same backends. Exposes window.StickyCartRx.apply(scope, opts).
   ========================================================================== */
(function () {
  'use strict';

  var PHARMA_MSG_URL = 'https://supertails-backend.el.r.appspot.com/pharmacy/appointmentStatusMsg/cart';
  var UPLOAD_URL = 'https://supertails-backend.el.r.appspot.com/upload';
  var MAX_BYTES = 8 * 1024 * 1024;
  var MAX_FILES = 5; // up to 5 prescriptions per order
  var ACCEPT_RE = /\.(jpe?g|pdf)$/i;

  // Number of Rx (prescription) line items currently in the cart. The block carries data-rx-count
  // (data-rx-lines lives on the content root, not here — reading it off the block returned 0).
  function rxLineCount() {
    var b = document.querySelector('[data-st-rx-block]');
    return b ? (parseInt(b.getAttribute('data-rx-count'), 10) || 0) : 0;
  }

  // Business-hours window for the vet-call copy: the pharmacy message says "…by 7AM tomorrow"
  // outside hours vs an immediate call inside. Derive the spec's "inside"|"outside" from it.
  var bizHoursState = null;
  function deriveBizHours(vetCopy) {
    if (!vetCopy) return bizHoursState; // keep last known if copy unavailable
    bizHoursState = /tomorrow/i.test(vetCopy) ? 'outside' : 'inside';
    return bizHoursState;
  }

  function fetchPharmaMsg() {
    var cached = sessionStorage.getItem('stPharmaMsg');
    if (cached) {
      try { return Promise.resolve(JSON.parse(cached)); } catch (e) {}
    }
    return fetch(PHARMA_MSG_URL, { headers: { 'Content-Type': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var msg = (data && data.msg) || {};
        try { sessionStorage.setItem('stPharmaMsg', JSON.stringify(msg)); } catch (e) {}
        return msg;
      })
      .catch(function () { return {}; });
  }

  var PDF_THUMB =
    `<svg width="45" height="50" viewBox="0 0 47 60" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clip-path="url(#clip0_3268_4927)">
        <path
          d="M3.62402 0.400391H32.6875L46.5996 14.3125V56.3535C46.5995 57.0391 46.3032 57.7396 45.8984 58.2559L45.6133 58.0283L45.1133 58.6533L45.3271 58.8242C44.7808 59.2497 44.1568 59.4609 43.376 59.4609H3.62402C2.67784 59.4609 1.96121 59.1516 1.33496 58.5254C0.813412 58.0037 0.400487 57.1666 0.400391 56.3535V3.50781C0.400391 1.86662 1.84902 0.400586 3.62402 0.400391Z"
          fill="#FCF9F9" stroke="#FF312A" stroke-width="0.8" />
        <path
          d="M32.7217 0.440002C32.8093 0.401653 32.9252 0.403321 33.0391 0.517151L46.5039 13.982L46.5264 13.9996C46.5724 14.0341 46.6126 14.1091 46.5576 14.2427C46.5013 14.3795 46.3914 14.4478 46.2988 14.4478H35.6602C33.9021 14.4478 32.5527 12.9988 32.5527 11.3404V0.701721C32.5527 0.578926 32.6238 0.482967 32.7217 0.440002Z"
          fill="#EEEEEE" stroke="#FF312A" stroke-width="0.8" />
        <path
          d="M0 28.0598V48.1692H37.0622C38.1144 48.1692 38.9328 47.8185 39.6343 47.117C40.2189 46.5324 40.5697 45.5971 40.5697 44.6617V31.5672C40.5697 30.515 40.2189 29.6966 39.6343 28.9951C38.9328 28.4105 38.1144 28.0598 37.0622 28.0598H0Z"
          fill="#FF312A" />
        <path
          d="M5.26009 23.5001C4.32476 23.5001 4.32476 24.903 5.26009 24.903H35.4243C36.3596 24.903 36.3596 23.5001 35.4243 23.5001H5.26009ZM5.26009 18.1219C4.32476 18.1219 4.32476 19.5249 5.26009 19.5249H35.4243C36.3596 19.5249 36.3596 18.1219 35.4243 18.1219H5.26009Z"
          fill="#B5B5B5" />
        <path
          d="M6.07812 43.4926V32.6195C7.24728 32.6195 10.8717 32.6195 11.807 32.7364C14.8468 33.321 14.9637 36.5946 13.4438 38.1145C12.3916 39.4006 9.46867 39.0499 7.48111 39.0499V43.4926H6.07812ZM7.48111 37.7638C8.8841 37.7638 11.4562 37.9976 12.2746 37.2961C13.21 36.4777 13.0931 34.3732 11.6901 34.0225C11.1055 33.9056 8.29952 33.9056 7.48111 33.9056V37.7638ZM16.2498 43.4926V32.6195C17.5358 32.6195 20.8095 32.6195 21.8617 32.8533C26.0707 33.7887 26.0707 40.9205 23.1478 42.7911C22.3294 43.3757 21.2771 43.4926 20.108 43.4926H16.2498ZM17.6528 42.2066C20.4587 42.2066 22.5632 42.6742 23.3816 40.1021C23.9662 38.3484 23.8493 34.9578 21.7448 34.1394C20.9264 33.7887 18.705 33.9056 17.6528 33.9056V42.2066ZM27.2398 43.4926V32.6195H34.6055V33.9056H28.6428V37.2961H33.7871V38.5822H28.6428V43.4926H27.2398Z"
          fill="white" stroke="white" stroke-width="0.105224" />
      </g>
      <defs>
        <clipPath id="clip0_3268_4927">
          <rect width="47" height="59.8607" fill="white" />
        </clipPath>
      </defs>
    </svg>`;

  function apply(scope, opts) {
    scope = scope || document;
    opts = opts || {};

    var block = scope.querySelector('[data-st-rx-block]');

    // Per-render: vet copy + open triggers (only when the cart has Rx items).
    if (block) {
      var root = scope.querySelector('[data-st-content-root]');
      var rxLines = (root ? root.getAttribute('data-rx-lines') || '' : '')
        .split(',').map(function (s) { return s.trim(); }).filter(Boolean);

      var vetEl = block.querySelector('[data-st-rx-vet]');
      if (vetEl) {
        fetchPharmaMsg().then(function (msg) {
          // Backend copy may contain markup (e.g. "<b>10 minutes</b>") — render as HTML.
          if (msg && msg.isNotAdded) vetEl.innerHTML = msg.isNotAdded;
        });
      }

      // Stash the latest context on the (global) overlay for its bound handlers.
      var ov = document.querySelector('[data-st-rx-overlay]');
      if (ov) {
        ov.__stRxLines = rxLines;
        ov.__stOnChange = opts.onChange;
      }

      var uploadedCount = parseInt(block.getAttribute('data-rx-uploaded'), 10) || 0;
      var openers = block.querySelectorAll('[data-st-rx-open]');
      for (var i = 0; i < openers.length; i++) {
        openers[i].addEventListener('click', function (e) {
          var A = window.StickyCartAnalytics;
          if (A) {
            var t = e.currentTarget;
            if (t.classList.contains('st-cart__rx-addmore') && typeof A.rxAddMore === 'function') {
              A.rxAddMore(uploadedCount, rxLines.length);
            } else if (typeof A.rxUploadInitiated === 'function') {
              A.rxUploadInitiated(rxLines.length, bizHoursState);
            }
          }
          openModal();
        });
      }

      // × on an uploaded thumbnail: drop that URL from the comma-separated set, re-attach
      // the remaining list to every Rx line, then refresh the drawer.
      var removeBtns = block.querySelectorAll('[data-st-rx-thumb-remove]');
      for (var r = 0; r < removeBtns.length; r++) {
        removeBtns[r].addEventListener('click', function (e) {
          var thumb = e.currentTarget.closest('[data-st-rx-thumb]');
          var url = thumb ? thumb.getAttribute('data-url') : '';
          if (!url) return;
          var current = (block.getAttribute('data-rx-urls') || '')
            .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
          var next = current.filter(function (u) { return u !== url; });
          e.currentTarget.disabled = true;
          attachToLines(next.join(','), rxLines).then(function () {
            if (typeof opts.onChange === 'function') opts.onChange();
          });
        });
      }

      // Analytics: Rx Banner Displayed (empty) or Rx Uploaded State Viewed (after upload),
      // once per open. Vet copy comes from the pharmacy appointment-status API.
      var A = window.StickyCartAnalytics;
      if (A) {
        var rxProducts = [];
        Array.prototype.forEach.call(scope.querySelectorAll('[data-st-items] .st-cart__item[data-rx="true"]'), function (n) {
          rxProducts.push({
            product_id: n.getAttribute('data-product-id'), variant_id: n.getAttribute('data-variant-id'),
            sku_id: n.getAttribute('data-skuid'), brand: n.getAttribute('data-brand') || null,
            product_name: (n.querySelector('.st-cart__item-name') || {}).textContent || null
          });
        });
        fetchPharmaMsg().then(function (msg) {
          var vetCopy = ((msg && msg.isNotAdded) || '').replace(/<[^>]*>/g, '').trim();
          var hours = deriveBizHours(vetCopy);
          if (uploadedCount > 0) {
            if (typeof A.rxUploadedStateViewed === 'function') A.rxUploadedStateViewed(uploadedCount, rxLines.length);
          } else if (typeof A.rxBannerDisplayed === 'function') {
            A.rxBannerDisplayed(rxLines.length, rxProducts, hours, vetCopy);
          }
        });
      }
    }

    // Overlay handlers bound once (the modal lives in the persistent shell).
    bindOverlayOnce();
  }

  function openModal() {
    var overlay = document.querySelector('[data-st-rx-overlay]');
    if (!overlay) return;
    // Pre-populate with already-uploaded prescriptions (the "Add more" flow), capped at
    // MAX_FILES, so the user adds to the existing set rather than replacing it.
    var items = [];
    var block = document.querySelector('[data-st-rx-block]');
    var urlsAttr = block ? (block.getAttribute('data-rx-urls') || '') : '';
    urlsAttr.split(',').forEach(function (u) {
      u = (u || '').trim();
      if (u && items.length < MAX_FILES) items.push({ url: u });
    });
    overlay.__stItems = items;
    resetModal(overlay);
    renderFiles(overlay);
    overlay.hidden = false;
    if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.rxUploadModalViewed === 'function') {
      var cnt = block ? (parseInt(block.getAttribute('data-rx-count'), 10) || 0) : 0;
      window.StickyCartAnalytics.rxUploadModalViewed(cnt, bizHoursState);
    }
  }

  function closeModal() {
    var overlay = document.querySelector('[data-st-rx-overlay]');
    if (overlay) overlay.hidden = true;
  }

  function resetModal(overlay) {
    var fileInput = overlay.querySelector('[data-st-rx-file]');
    var callout = overlay.querySelector('[data-st-rx-callout]');
    var continueBtn = overlay.querySelector('[data-st-rx-continue]');
    var errorEl = overlay.querySelector('[data-st-rx-error]');
    if (fileInput) fileInput.value = '';
    if (callout) callout.hidden = false;
    if (continueBtn) { continueBtn.disabled = true; continueBtn.textContent = 'Continue'; }
    if (errorEl) errorEl.hidden = true;
  }

  // One preview tile: image thumbnail (cached object URL for new files) or PDF glyph.
  function tileHtml(item, index) {
    var isPdf = item.url ? /\.pdf$/i.test(item.url) : (item.file && /\.pdf$/i.test(item.file.name));
    var inner;
    if (isPdf) inner = PDF_THUMB;
    else inner = '<img src="' + (item.url || item._previewUrl) + '" alt="prescription">';
    return (
      '<div class="st-cart__rx-preview" data-st-rx-tile data-index="' + index + '">' +
        '<div class="st-cart__rx-preview-thumb">' + inner + '</div>' +
        '<button type="button" class="st-cart__rx-preview-x" data-st-rx-tile-remove aria-label="Remove">' +
          '<svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">' +
          '<path d="M6.5625 2.1875L2.1875 6.5625" stroke="#717171" stroke-width="1.09375" stroke-linecap="round" stroke-linejoin="round" />' +
          '<path d="M2.1875 2.1875L6.5625 6.5625" stroke="#717171" stroke-width="1.09375" stroke-linecap="round" stroke-linejoin="round" />' +
          '</svg>' +
        '</button>' +
      '</div>'
    );
  }

  // Re-render the preview tiles before the "+" drop tile; hide the drop tile at the cap;
  // enable Continue when there's at least one prescription.
  function renderFiles(overlay) {
    var filesWrap = overlay.querySelector('[data-st-rx-files]');
    var drop = overlay.querySelector('[data-st-rx-drop]');
    var continueBtn = overlay.querySelector('[data-st-rx-continue]');
    var callout = overlay.querySelector('[data-st-rx-callout]');
    if (!filesWrap) return;
    var old = filesWrap.querySelectorAll('[data-st-rx-tile]');
    for (var i = 0; i < old.length; i++) old[i].remove();
    var items = overlay.__stItems || [];
    var html = '';
    items.forEach(function (it, idx) { html += tileHtml(it, idx); });
    if (drop) {
      if (html) drop.insertAdjacentHTML('beforebegin', html);
      drop.hidden = items.length >= MAX_FILES;
    }
    // The "Accepted formats" callout (with its pointer to the full-width drop box) is an
    // empty-state hint — hide it once at least one prescription is added (Figma 109-8186).
    if (callout) callout.hidden = items.length > 0;
    if (continueBtn) continueBtn.disabled = items.length === 0;
  }

  function bindOverlayOnce() {
    var overlay = document.querySelector('[data-st-rx-overlay]');
    if (!overlay || overlay.__stBound) return;
    overlay.__stBound = true;

    var fileInput = overlay.querySelector('[data-st-rx-file]');
    var filesWrap = overlay.querySelector('[data-st-rx-files]');
    var continueBtn = overlay.querySelector('[data-st-rx-continue]');
    var errorEl = overlay.querySelector('[data-st-rx-error]');

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
    }

    var closers = overlay.querySelectorAll('[data-st-rx-close]');
    for (var i = 0; i < closers.length; i++) closers[i].addEventListener('click', closeModal);

    // Multi-file select: validate each, append up to the MAX_FILES cap.
    if (fileInput) {
      fileInput.addEventListener('change', function () {
        var files = fileInput.files;
        if (!files || !files.length) return;
        if (errorEl) errorEl.hidden = true;
        var items = overlay.__stItems || (overlay.__stItems = []);
        var rejected = null, rejectedExt = null, capped = false;
        for (var j = 0; j < files.length; j++) {
          if (items.length >= MAX_FILES) { capped = true; break; }
          var f = files[j];
          var ext = (f.name.split('.').pop() || '').toLowerCase();
          if (!ACCEPT_RE.test(f.name)) { rejected = 'invalid_format'; rejectedExt = ext; continue; }
          if (f.size > MAX_BYTES) { rejected = 'size_exceeded'; rejectedExt = ext; continue; }
          items.push({ file: f, _previewUrl: /\.pdf$/i.test(f.name) ? null : URL.createObjectURL(f) });
        }
        fileInput.value = ''; // allow re-selecting the same file after a remove
        if (capped) showError('You can upload up to ' + MAX_FILES + ' prescriptions.');
        else if (rejected) showError('Only JPG, JPEG or PDF files under 8MB are accepted.');
        if (rejected && window.StickyCartAnalytics && typeof window.StickyCartAnalytics.rxUploadFailed === 'function') {
          window.StickyCartAnalytics.rxUploadFailed(rejected, rejectedExt, rxLineCount());
        }
        renderFiles(overlay);
      });
    }

    // Remove a tile (delegated, since tiles are re-rendered).
    if (filesWrap) {
      filesWrap.addEventListener('click', function (e) {
        var rm = e.target.closest && e.target.closest('[data-st-rx-tile-remove]');
        if (!rm) return;
        var tile = rm.closest('[data-st-rx-tile]');
        var idx = tile ? parseInt(tile.getAttribute('data-index'), 10) : NaN;
        var items = overlay.__stItems || [];
        if (!isNaN(idx) && idx >= 0 && idx < items.length) {
          var removed = items.splice(idx, 1)[0];
          if (removed && removed._previewUrl) { try { URL.revokeObjectURL(removed._previewUrl); } catch (er) {} }
        }
        if (errorEl) errorEl.hidden = true;
        renderFiles(overlay);
      });
    }

    if (continueBtn) {
      continueBtn.addEventListener('click', function () {
        var items = overlay.__stItems || [];
        if (!items.length) return;
        continueBtn.disabled = true;
        continueBtn.textContent = 'Uploading…';
        // Upload the new files; existing URLs pass straight through. Preserve order.
        Promise.all(items.map(function (it) {
          return it.url ? Promise.resolve(it.url) : uploadFile(it.file);
        }))
          .then(function (urls) {
            var clean = urls.filter(Boolean);
            if (!clean.length) throw new Error('no url');
            // Analytics: one Rx Prescription Uploaded per newly-uploaded file.
            var A = window.StickyCartAnalytics;
            if (A && typeof A.rxUploaded === 'function') {
              items.forEach(function (it) {
                if (!it.url && it.file) A.rxUploaded((it.file.name.split('.').pop() || '').toLowerCase(), rxLineCount(), clean.length);
              });
            }
            // Attach the full comma-separated set to every Rx line (parity with the section,
            // which reads the list off the first Rx line).
            return attachToLines(clean.join(','), overlay.__stRxLines || []);
          })
          .then(function () {
            closeModal();
            if (typeof overlay.__stOnChange === 'function') overlay.__stOnChange();
          })
          .catch(function (err) {
            if (window.console) console.error('[StickyCart] rx upload', err);
            continueBtn.disabled = false;
            continueBtn.textContent = 'Continue';
            showError('Upload failed. Please try again.');
            if (window.StickyCartAnalytics && typeof window.StickyCartAnalytics.rxUploadFailed === 'function') {
              window.StickyCartAnalytics.rxUploadFailed('network_error', null, rxLineCount());
            }
          });
      });
    }
  }

  function uploadFile(file) {
    var form = new FormData();
    form.append('file', file, file.name);
    return fetch(UPLOAD_URL, { method: 'POST', body: form, redirect: 'follow' })
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var res = {};
        try { res = JSON.parse(text); } catch (e) {}
        return res && res.url ? res.url : null;
      });
  }

  // Attach the prescription URL to each Rx line item (mirrors the existing flow).
  function attachToLines(url, lines) {
    if (!lines.length) return Promise.resolve();
    return lines.reduce(function (chain, line) {
      return chain.then(function () {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ line: parseInt(line, 10), properties: { prescription_file: url } })
        });
      });
    }, Promise.resolve());
  }

  window.StickyCartRx = { apply: apply };
})();
